import styled from 'styled-components'
import { Box } from '../../atoms/Box'

// Re-export shared styles from SectionAnalysisStyles
export {
  SectionAnalysisTabsContainer as TabsContainer,
  SectionAnalysisTabButton as TabButton,
  SectionAnalysisContentContainer as ContentContainer,
  SectionAnalysisLoadingContainer as LoadingContainer,
  SectionAnalysisErrorContainer as ErrorContainer,
  SectionAnalysisTabContentContainer as TabContentContainer,
  SectionAnalysisFilterSection as FilterSection,
  SectionAnalysisFilterBar as FilterBar,
  SectionAnalysisLocationFilters as LocationFilters,
  SectionAnalysisLocationBadge as LocationBadge,
} from '../../molecules/SectionAnalysisStyles/SectionAnalysisStyles'

// Electricity-specific container
export const ElectricityContainer = styled(Box)`
  width: 100%;
  background: ${({ theme }) => theme.colors.background.primary};
  padding-bottom: 48px;
`

// Category selector wrapper (relative positioning for panel)
export const CategorySelectorWrapper = styled.div`
  position: relative;
`

// Trigger button - mirrors the ItemRow look for selected category
export const CategoryTrigger = styled.button`
  display: flex;
  align-items: center;
  gap: 8px;
  padding: 4px 8px 4px 4px;
  border: 1px solid ${({ theme }) => theme.colors.border.default};
  border-radius: ${({ theme }) => theme.borderRadius.default};
  background: ${({ theme }) => theme.colors.background.primary};
  cursor: pointer;
  text-align: left;

  &:hover {
    border-color: ${({ theme }) => theme.colors.border.focus};
  }

  &:focus {
    outline: none;
    border-color: ${({ theme }) => theme.colors.primary};
  }
`

export const TriggerChevron = styled.span<{ $open: boolean }>`
  display: flex;
  align-items: center;
  transition: ${({ theme }) => theme.transitions.fast};
  transform: rotate(${({ $open }) => ($open ? '180deg' : '0deg')});
  flex-shrink: 0;
  color: ${({ theme }) => theme.colors.text.tertiary};
`

// Overlay to catch clicks outside panel
export const PanelOverlay = styled.div`
  position: fixed;
  top: 0;
  left: 0;
  right: 0;
  bottom: 0;
  z-index: ${({ theme }) => theme.zIndex.dropdown - 1};
`

// Two-column panel container
export const PanelContainer = styled.div`
  position: absolute;
  top: calc(100% + 4px);
  left: 0;
  z-index: ${({ theme }) => theme.zIndex.dropdown};
  background: ${({ theme }) => theme.colors.background.primary};
  border: 1px solid ${({ theme }) => theme.colors.border.light};
  border-radius: ${({ theme }) => theme.borderRadius.medium};
  box-shadow: ${({ theme }) => theme.shadows.lg};
  display: grid;
  grid-template-columns: 1fr auto 1fr;
  min-width: 620px;
  max-height: 480px;
  overflow-y: auto;

  @media (max-width: 768px) {
    grid-template-columns: 1fr;
    min-width: 300px;
    max-width: calc(100vw - 40px);
    right: 0;
    left: auto;
  }
`

// Vertical dashed separator between columns
export const ColumnSeparator = styled.div`
  width: 1px;
  background: repeating-linear-gradient(
    to bottom,
    ${({ theme }) => theme.colors.border.light} 0px,
    ${({ theme }) => theme.colors.border.light} 4px,
    transparent 4px,
    transparent 8px
  );
  margin: 10px 0;

  @media (max-width: 768px) {
    display: none;
  }
`

// Single column inside panel
export const PanelColumn = styled.div`
  display: flex;
  flex-direction: column;
  padding: 8px;
`

// Column header with colored background
export const ColumnHeader = styled.div<{
  $variant: 'residential' | 'commercial'
}>`
  display: flex;
  align-items: center;
  gap: 8px;
  padding: 8px 10px;
  border-radius: ${({ theme }) => theme.borderRadius.default};
  margin-bottom: 4px;
  background: ${({ theme, $variant }) =>
    $variant === 'residential'
      ? theme.colors.primaryLight
      : theme.colors.warningLight};
`

export const ColumnHeaderIcon = styled.span<{
  $variant: 'residential' | 'commercial'
}>`
  display: flex;
  align-items: center;
  justify-content: center;
  color: ${({ theme, $variant }) =>
    $variant === 'residential' ? theme.colors.primary : theme.colors.warning};
`

export const ColumnHeaderText = styled.div`
  display: flex;
  flex-direction: column;
`

export const ColumnHeaderTitle = styled.span<{
  $variant: 'residential' | 'commercial'
}>`
  font-size: 11px;
  font-weight: 700;
  color: ${({ theme, $variant }) =>
    $variant === 'residential' ? theme.colors.primary : theme.colors.warning};
`

export const ColumnHeaderSubtitle = styled.span`
  font-size: 9px;
  color: ${({ theme }) => theme.colors.text.tertiary};
`

// Individual category item row
export const ItemRow = styled.button<{ $selected: boolean }>`
  display: flex;
  align-items: center;
  gap: 8px;
  padding: 6px 8px;
  border: 1.5px solid
    ${({ theme, $selected }) =>
      $selected ? theme.colors.primary : 'transparent'};
  border-radius: ${({ theme }) => theme.borderRadius.default};
  background: ${({ theme, $selected }) =>
    $selected ? theme.colors.primaryLight : 'transparent'};
  cursor: pointer;
  text-align: left;
  width: 100%;
  position: relative;

  &:hover {
    background: ${({ theme, $selected }) =>
      $selected
        ? theme.colors.primaryLight
        : theme.colors.background.secondary};
  }
`

// Icon square for each item
export const ItemIcon = styled.span<{ $bgColor: string }>`
  display: flex;
  align-items: center;
  justify-content: center;
  width: 28px;
  height: 28px;
  border-radius: ${({ theme }) => theme.borderRadius.default};
  background: ${({ $bgColor }) => $bgColor};
  flex-shrink: 0;
`

// Item text container
export const ItemTextContainer = styled.div`
  display: flex;
  flex-direction: column;
  flex: 1;
  min-width: 0;
`

// Category code (bold, e.g. "H4")
export const ItemCode = styled.span<{ $selected: boolean }>`
  font-size: 11px;
  font-weight: 700;
  color: ${({ theme, $selected }) =>
    $selected ? theme.colors.primary : theme.colors.text.primary};
`

// Short label text
export const ItemLabel = styled.span<{ $selected: boolean }>`
  font-size: 10px;
  color: ${({ theme, $selected }) =>
    $selected ? theme.colors.primary : theme.colors.text.secondary};
  overflow: hidden;
  text-overflow: ellipsis;
  white-space: nowrap;
`

// kWh pill badge
export const KwhBadge = styled.span<{ $textColor: string; $bgColor: string }>`
  font-size: 9px;
  font-weight: 600;
  padding: 2px 8px;
  border-radius: 9px;
  white-space: nowrap;
  flex-shrink: 0;
  color: ${({ $textColor }) => $textColor};
  background: ${({ $bgColor }) => $bgColor};
`

// Checkmark circle for selected item
export const CheckmarkBadge = styled.span`
  display: flex;
  align-items: center;
  justify-content: center;
  width: 16px;
  height: 16px;
  border-radius: ${({ theme }) => theme.borderRadius.circle};
  background: ${({ theme }) => theme.colors.primary};
  flex-shrink: 0;
`
